import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.loaderNode = document.getElementById("global-ajax-loader")

    // Explicit binding locks the memory reference for reliable cleanup
    this.clickHandler = this.handleRemoteClick.bind(this)
    document.addEventListener("click", this.clickHandler)

    this.activeModalsStack = []
    this.isLoading = false
  }

  disconnect() {
    // This now matches perfectly and cleans up memory flawlessly
    document.removeEventListener("click", this.clickHandler)
  }

  handleRemoteClick(event) {
    const trigger = event.target.closest(".modal-ajax")
    if (!trigger) return

    event.preventDefault()
    event.stopPropagation()

    if (this.isLoading) return

    const requestUrl = trigger.getAttribute("href")
    if (!requestUrl || requestUrl === "#") return

    document.querySelectorAll('.tooltip, .popover').forEach(el => el.remove())

    const modalTitle = trigger.dataset.modalTitle || "Givsum Action"
    const modalSize = trigger.dataset.modalSize || ""

    this.showSpinner()
    this.fetchAndSpawn(requestUrl, modalTitle, modalSize)
  }

  async fetchAndSpawn(url, title, size) {
    try {
      this.isLoading = true // Lock execution
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" }
      })
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)

      const responseMarkup = await response.text()
      this.hideSpinner()

      this.createNewModalInstance(responseMarkup, title, size)

    } catch (error) {
      console.error("Intercept error processing pipeline:", error)
      this.hideSpinner()
      this.renderTemporaryErrorToast()
    } finally {
      this.isLoading = false
    }
  }

  createNewModalInstance(markup, initialTitle, size) {
    const modalId = `modal-${Date.now()}`

    // Parse the incoming markup response block context
    const parser = new DOMParser()
    const doc = parser.parseFromString(markup, "text/html")

    // Look for backend title override parameter configurations
    const titleOverrideNode = doc.querySelector("[data-modal-title-override]")
    let finalTitle = initialTitle
    if (titleOverrideNode) {
      finalTitle = titleOverrideNode.textContent.trim()
      titleOverrideNode.remove()
    }

    const footerNode = doc.querySelector(".modal-footer")
    let footerHTML = ""
    if (footerNode) {
      footerHTML = footerNode.outerHTML
      footerNode.remove()
    }

    const bodyHTML = doc.body ? doc.body.innerHTML : markup

    const template = `
      <div class="modal fade" id="${modalId}" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered ${size}">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${finalTitle}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              ${bodyHTML}
            </div>
            ${footerHTML}
          </div>
        </div>
      </div>
    `

    document.body.insertAdjacentHTML("beforeend", template)
    const element = document.getElementById(modalId)

    // Manage visual depth indexes explicitly if multiple layers stack
    if (this.activeModalsStack.length > 0) {
      element.classList.add("stack-level-up")
      element.style.zIndex = 1055 + (this.activeModalsStack.length * 10)
    }

    const bsInstance = new bootstrap.Modal(element, {
      backdrop: this.activeModalsStack.length > 0 ? false : true // Prevent gray backdrops from getting overly dark
    })
    bsInstance.show()

    this.activeModalsStack.push({ element, instance: bsInstance })

    element.addEventListener("hidden.bs.modal", () => {
      bsInstance.dispose()
      element.remove()
      this.activeModalsStack = this.activeModalsStack.filter(item => item.element !== element)
      if (this.activeModalsStack.length > 0) {
        document.body.classList.add("modal-open")
      }
    })
  }

  renderTemporaryErrorToast() {
    const alertId = `err-alert-${Date.now()}`
    const errorAlertHTML = `
      <div id="${alertId}" class="position-fixed top-0 start-50 translate-middle-x mt-4 p-3 alert alert-warning" style="z-index: 1100;">
        <div class="alert-icon">
          <i class="fa fa-info-circle"></i>
        </div>
        <div class="alert-body">
          <div class="alert-heading alert-message-text"> The content could not be loaded. </div>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML("beforeend", errorAlertHTML)

    setTimeout(() => {
      const node = document.getElementById(alertId)
      if (node) node.remove()
    }, 3500)
  }

  showSpinner() {
    if (this.loaderNode) this.loaderNode.classList.remove("d-none")
  }

  hideSpinner() {
    if (this.loaderNode) this.loaderNode.classList.add("d-none")
  }
}